import {
  callbackLeadRecordQuery,
  CallbackLeadRecord,
  CallbackLeadRecordBooking,
} from "@/app/CallbackLeads/GraphQL/callbackLeadRecordQuery";
import { Badge } from "@/components/catalyst/badge";
import { Link } from "@/components/catalyst/link";
import { Strong, Text } from "@/components/catalyst/text";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import { formatDateTimeFromNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { CallbackLeadSource, RelocationStatus } from "@/gql/graphql";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import dayjs from "dayjs";
import { ReactNode } from "react";
import { useTranslations } from "use-intl";

export function CallbackLeadSummary({ leadId }: { leadId: string }) {
  const t = useTranslations("callbackLead");
  const tRelocation = useTranslations("relocation");
  const tVehicle = useTranslations("vehicle");
  const { data } = useSuspenseGqlQuery(callbackLeadRecordQuery, { id: leadId });

  const lead = data.callbackLead as CallbackLeadRecord;
  const booking = lead.booking;
  const relocation = booking?.relocation;
  const vehicle = booking?.vehicle;

  const hireUnits = booking
    ? dayjs(booking.deliver_at).diff(dayjs(booking.depart_at), "day")
    : 0;
  const age = booking?.date_of_birth
    ? dayjs().diff(dayjs(booking.date_of_birth), "year")
    : null;

  return (
    <div className="space-y-4 rounded-md bg-gray-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Strong>{lead.name}</Strong>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <CopyToClipboard
              text={lead.phone}
              title={t("actions.copyPhone")}
              className="text-blue-500"
            />
            {lead.email ? (
              <CopyToClipboard
                text={lead.email}
                title={t("actions.copyEmail")}
                className="text-blue-500 underline"
              />
            ) : null}
          </div>
        </div>
        <div className="text-right">
          {booking ? (
            <Link
              to={"/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"}
              params={(prev) => ({ ...prev, bookingId: booking.id })}
              viewTransition
              className="text-sm text-blue-500 underline"
            >
              {booking.reference}
            </Link>
          ) : null}
          <Text className="text-xs">
            {lead.source === CallbackLeadSource.SupplierCancellation
              ? t("summary.cancelled", {
                  when: formatDateTimeFromNow(lead.created_at),
                })
              : booking
                ? t("summary.abandoned", {
                    when: formatDateTimeFromNow(booking.created_at),
                  })
                : t("summary.created", {
                    when: formatDateTimeFromNow(lead.created_at),
                  })}
          </Text>
        </div>
      </div>

      {booking && relocation && vehicle ? (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-gray-200 pt-3 sm:grid-cols-3">
          <Detail label={t("summary.trip")}>
            {booking.departureOffice.name} → {booking.deliveryOffice.name}
          </Detail>
          <Detail label={t("summary.dates")}>
            {formatDate(booking.depart_at)} → {formatDate(booking.deliver_at)}
            <span className="text-gray-500">
              {" "}
              ({t("summary.days", { count: hireUnits })})
            </span>
          </Detail>
          <Detail label={t("summary.vehicle")}>
            {vehicle.name}
            <span className="text-gray-500">
              {" "}
              · {tVehicle(`type.${vehicle.type}`)} ·{" "}
              {tVehicle(`transmission.${vehicle.transmission}`)}
            </span>
          </Detail>
          <Detail label={t("summary.supplier")}>{booking.supplier.name}</Detail>
          <Detail label={t("summary.travellers")}>
            {booking.number_of_travellers}
            <span className="text-gray-500">
              {" "}
              / {vehicle.seatbelts} {t("summary.seatbelts")}
            </span>
            {age ? (
              <span className="text-gray-500">
                {" "}
                · {t("summary.driverAge", { age })}
              </span>
            ) : null}
          </Detail>
          <Detail label={t("summary.distance")}>
            {booking.distance_allowed
              ? `${booking.distance_allowed} ${tRelocation(`distanceUnit.${booking.measurement}`)}`
              : t("summary.unlimited")}
          </Detail>
          <Detail label={t("summary.payNow")}>
            <Strong>
              {formatCurrency(
                booking.charge_summary.pay_now_total_amount,
                booking.currency,
              )}
            </Strong>
            {booking.charge_summary.pay_later_total_amount > 0 ? (
              <span className="text-gray-500">
                {" "}
                {t("summary.payLater", {
                  amount: formatCurrency(
                    booking.charge_summary.pay_later_total_amount,
                    booking.currency,
                  ),
                })}
              </span>
            ) : null}
          </Detail>
          <Detail label={t("summary.dealStatus")}>
            <div className="flex flex-wrap items-center gap-x-2">
              <Badge
                color={
                  relocation.status === RelocationStatus.Ready ? "green" : "red"
                }
              >
                {tRelocation(`statusLabels.${relocation.status}`)}
              </Badge>
              {relocation.status === RelocationStatus.Ready ? (
                <span className="text-gray-500">
                  {t("summary.remaining", { count: relocation.count })}
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2">
              <Link
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to={
                  "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
                }
                params={(prev) => ({ ...prev, relocationId: relocation.id })}
                viewTransition
                className="text-blue-500 underline"
              >
                {relocation.reference}
              </Link>
              <a
                className="inline-flex items-center gap-x-1 text-blue-500 underline"
                href={relocation.customer_url}
                target="_blank"
                rel="noreferrer"
                title={t("summary.publicListing")}
              >
                {t("summary.publicListing")}
                <ArrowTopRightOnSquareIcon className="size-3" />
              </a>
            </div>
          </Detail>
          <Detail label={t("summary.availability")}>
            {formatDate(relocation.available_from_date)} →{" "}
            {formatDate(relocation.available_to_date)}
          </Detail>
        </dl>
      ) : null}

      {booking?.inclusions.length ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3">
          <span className="text-xs text-gray-500">
            {t("summary.inclusions")}
          </span>
          {booking.inclusions.map((inclusion) => (
            <Badge key={inclusion.id} color="blue">
              {tRelocation(`inclusions.type.${inclusion.type}`)}
              {inclusion.value
                ? ` ${formatCurrency(inclusion.value, booking.currency)}`
                : null}
              {inclusion.has_free_tank ? ` · ${t("summary.freeTank")}` : null}
            </Badge>
          ))}
        </div>
      ) : null}

      {booking?.customer_note ? (
        <div className="border-t border-gray-200 pt-3">
          <span className="text-xs text-gray-500">
            {t("summary.customerNote")}
          </span>
          <Text className="whitespace-pre-wrap text-sm">
            {booking.customer_note}
          </Text>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-3 text-xs text-gray-500">
        <span>
          {t("summary.callHistory", {
            attempts: lead.contact_attempts,
            lastCalled: lead.last_contacted_at
              ? formatDateTimeFromNow(lead.last_contacted_at)
              : t("summary.never"),
          })}
        </span>
        {booking ? (
          <a
            className="text-blue-500 underline"
            href={booking.customer_url}
            target="_blank"
            rel="noreferrer"
          >
            {t("summary.checkoutLink")}
          </a>
        ) : null}
      </div>

      {lead.bookings_count > 1 ? (
        <div className="border-t border-gray-200 pt-3">
          <span className="text-xs text-gray-500">
            {t("summary.otherIncomplete", { count: lead.bookings_count - 1 })}
          </span>
          <div className="mt-1 space-y-1">
            {lead.bookings
              .filter(
                (other: CallbackLeadRecordBooking) => other.id !== booking?.id,
              )
              .map((other: CallbackLeadRecordBooking) => (
                <div key={other.id} className="flex gap-x-3 text-xs">
                  <span className="text-gray-500">{other.reference}</span>
                  <span>
                    {other.departureOffice.name} → {other.deliveryOffice.name}
                  </span>
                  <span>
                    {formatCurrency(
                      other.charge_summary.total_amount,
                      other.currency,
                    )}
                  </span>
                  <span className="text-gray-500">
                    {formatDate(other.created_at)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}
